import {
  MINI_DUNGEON_ENCOUNTERS,
  MINI_DUNGEON_ID,
  MINI_DUNGEON_NAME,
  type EncounterBlueprint,
} from '../data/dungeon';
import {
  clampDungeonLevel,
  isDungeonMutationId,
  migrateLegacyDifficulty,
  type DungeonMutationId,
} from '../data/dungeonProgression';
import {
  defaultCharacterCombatStats,
  type CharacterCombatStats,
} from './characterStats';
import { createEncounter, type CreateEncounterOptions } from './combat';
import {
  absorbEncounterIntoPlayLog,
  appendPlayLog,
  formatEncounterBanner,
  formatRewardLog,
} from './combatLog';
import type { EquipmentSnapshot } from './equipmentCombat';
import type { GeneratedLootItem } from './loot';
import { generateEncounterEquipmentLoot } from './lootRewards';
import type { SeededRng } from './rng';
import {
  PROVISIONAL_PLAYER_MAX_HP,
  STARTING_UNLOCKED_DICE_SLOTS,
  type EncounterState,
  type PermanentProgressPlaceholder,
} from './types';

/** High-level dungeon run phase (separate from encounter combat phase). */
export type DungeonPhase = 'combat' | 'reward' | 'results';

export type DungeonOutcome = 'ongoing' | 'victory' | 'defeat';

export interface RunRewards {
  gold: number;
  materials: number;
  /** Count of boss chests collected. */
  bossChests: number;
}

export interface PendingEncounterReward {
  gold: number;
  materials: number;
  bossChest: boolean;
  encounterId: string;
  isBoss: boolean;
  /**
   * Equipment generated for this reward (elite/boss kill + chest).
   * Set once when entering reward phase — reload must not reroll.
   */
  equipment?: GeneratedLootItem[];
}

export interface DungeonRunStats {
  encountersCleared: number;
  enemiesDefeated: number;
  peakTempLevel: number;
}

/**
 * Persistent mini-dungeon run. Holds the active encounter plus run-wide
 * progression that must survive between fights (HP, temp XP, skills, offer rerolls).
 */
export interface DungeonRunState {
  dungeonId: string;
  dungeonName: string;
  seed: number;
  /** Linear encounter definitions for this run (serialised with the save). */
  encounters: EncounterBlueprint[];
  /** 0-based index into `encounters`. */
  encounterIndex: number;
  encounterCount: number;
  phase: DungeonPhase;
  outcome: DungeonOutcome;
  encounter: EncounterState;
  rewards: RunRewards;
  pendingReward: PendingEncounterReward | null;
  unlockedDiceSlots: number;
  stats: DungeonRunStats;
  /**
   * Run-wide play log (combat + progression). Capped; survives save/resume.
   * Newest lines last.
   */
  playLog: string[];
  /** How many lines of the current encounter.log have been absorbed into playLog. */
  playLogCursor: number;
  /**
   * Hook for future permanent progression (talents, inventory).
   * Not mutated by the mini-dungeon loop in Milestone 4.
   */
  permanent: PermanentProgressPlaceholder;
  /**
   * @deprecated M7-D2 — prefer dungeonLevel + mutation. Kept for legacy saves.
   */
  difficulty?: 'normal' | 'hard' | 'brutal';
  /** Numerical dungeon challenge / loot progression (M7-D2). */
  dungeonLevel: number;
  /** Standard / Hard / Brutal mutation (M7-D2). */
  mutation: DungeonMutationId;
  /**
   * Encounter ids that already produced equipment loot (idempotency / anti-reroll).
   */
  equipmentLootAwardedEncounterIds: string[];
  /**
   * Equipped loadout locked at dungeon entry (M7-C).
   * Combat uses this snapshot — not the live permanent inventory.
   * Legacy runs without a snapshot are treated as unequipped (safe).
   */
  equipmentSnapshot: EquipmentSnapshot | null;
}

export interface StartDungeonOptions {
  unlockedDiceSlots?: number;
  playerMaxHp?: number;
  /** Override encounter list (tests). Defaults to proto cave. */
  encounters?: readonly EncounterBlueprint[];
  dungeonId?: string;
  dungeonName?: string;
  /** Shared character combat stats (talents + future gear). */
  combatStats?: CharacterCombatStats;
  /** Permanent active skill unlocks for dungeon offers. */
  unlockedActiveSkillIds?: readonly string[];
  /** Numerical dungeon level (M7-D2). Defaults to 1. */
  dungeonLevel?: number;
  /** Mutation Standard/Hard/Brutal (M7-D2). Defaults to standard. */
  mutation?: DungeonMutationId;
  /**
   * @deprecated Prefer dungeonLevel + mutation.
   */
  difficulty?: 'normal' | 'hard' | 'brutal';
  /**
   * Equipment loadout snapshot at entry. Null / omitted → unequipped combat
   * (legacy active runs without a snapshot).
   */
  equipmentSnapshot?: EquipmentSnapshot | null;
}

function blueprintAt(
  encounters: readonly EncounterBlueprint[],
  index: number,
): EncounterBlueprint {
  const bp = encounters[index];
  if (!bp) {
    throw new Error(`No encounter blueprint at index ${index}`);
  }
  return bp;
}

function countDefeated(encounter: EncounterState): number {
  return encounter.enemies.filter((e) => e.hp <= 0).length;
}

function buildEncounter(
  rng: SeededRng,
  blueprint: EncounterBlueprint,
  options: {
    unlockedDiceSlots: number;
    playerMaxHp: number;
    playerHp?: number;
    progress?: EncounterState['progress'];
    tray?: EncounterState['tray'];
    combatStats?: CharacterCombatStats;
    unlockedActiveSkillIds?: readonly string[];
    dungeonLevel?: number;
    mutation?: DungeonMutationId;
    equipmentSnapshot?: EquipmentSnapshot | null;
  },
): EncounterState {
  const createOpts: CreateEncounterOptions = {
    unlockedDiceSlots: options.unlockedDiceSlots,
    enemyTemplateIds: blueprint.enemyTemplateIds,
    playerMaxHp: options.playerMaxHp,
    playerHp: options.playerHp,
    progress: options.progress,
    tray: options.tray,
    combatStats: options.combatStats,
    unlockedActiveSkillIds: options.unlockedActiveSkillIds,
    dungeonLevel: options.dungeonLevel ?? 1,
    mutation: options.mutation ?? 'standard',
    equipmentSnapshot: options.equipmentSnapshot ?? null,
  };
  return createEncounter(rng, createOpts);
}

/**
 * Starts a fresh mini-dungeon. Temporary skills/XP begin empty.
 * Combat dice auto-roll inside createEncounter.
 */
export function startDungeonRun(
  rng: SeededRng,
  seed: number,
  options: StartDungeonOptions = {},
): DungeonRunState {
  const encounters = options.encounters ?? MINI_DUNGEON_ENCOUNTERS;
  const unlockedDiceSlots =
    options.unlockedDiceSlots ?? STARTING_UNLOCKED_DICE_SLOTS;
  const combatStats = options.combatStats ?? defaultCharacterCombatStats();
  const unlockedActiveSkillIds = [
    ...(options.unlockedActiveSkillIds ?? combatStats.unlockedActiveSkillIds),
  ];
  const playerMaxHp =
    options.playerMaxHp ?? combatStats.maxHp ?? PROVISIONAL_PLAYER_MAX_HP;
  const migrated = options.difficulty
    ? migrateLegacyDifficulty(options.difficulty)
    : null;
  const dungeonLevel = clampDungeonLevel(
    options.dungeonLevel ?? migrated?.dungeonLevel ?? 1,
  );
  const mutation: DungeonMutationId = isDungeonMutationId(options.mutation ?? '')
    ? options.mutation!
    : (migrated?.mutation ?? 'standard');
  const first = blueprintAt(encounters, 0);
  const encounter = buildEncounter(rng, first, {
    unlockedDiceSlots,
    playerMaxHp,
    combatStats,
    unlockedActiveSkillIds,
    dungeonLevel,
    mutation,
    equipmentSnapshot: options.equipmentSnapshot ?? null,
  });
  const dungeonName = options.dungeonName ?? MINI_DUNGEON_NAME;
  const encounterCount = encounters.length;
  const absorbed = absorbEncounterIntoPlayLog(
    appendPlayLog(
      [`Entered ${dungeonName}`],
      [formatEncounterBanner(0, encounterCount, Boolean(first.isBoss))],
    ),
    0,
    encounter.log,
  );

  return {
    dungeonId: options.dungeonId ?? MINI_DUNGEON_ID,
    dungeonName,
    seed,
    encounters: encounters.map((bp) => ({
      ...bp,
      enemyTemplateIds: [...bp.enemyTemplateIds],
    })),
    encounterIndex: 0,
    encounterCount,
    phase: 'combat',
    outcome: 'ongoing',
    encounter,
    rewards: { gold: 0, materials: 0, bossChests: 0 },
    pendingReward: null,
    unlockedDiceSlots,
    stats: {
      encountersCleared: 0,
      enemiesDefeated: 0,
      peakTempLevel: encounter.progress.level,
    },
    playLog: absorbed.playLog,
    playLogCursor: absorbed.cursor,
    permanent: { unlockedDiceSlots },
    dungeonLevel,
    mutation,
    equipmentLootAwardedEncounterIds: [],
    equipmentSnapshot: options.equipmentSnapshot ?? null,
  };
}

/** Sync the run with the latest encounter state from combat + absorb play log. */
export function syncDungeonEncounter(
  run: DungeonRunState,
  encounter: EncounterState,
): DungeonRunState {
  const absorbed = absorbEncounterIntoPlayLog(
    run.playLog ?? [],
    run.playLogCursor ?? 0,
    encounter.log,
  );
  return {
    ...run,
    encounter,
    playLog: absorbed.playLog,
    playLogCursor: absorbed.cursor,
    stats: {
      ...run.stats,
      peakTempLevel: Math.max(run.stats.peakTempLevel, encounter.progress.level),
    },
  };
}

/**
 * After an encounter victory (and any pending level-ups are resolved),
 * open the reward collect step. Does not advance to the next fight yet.
 * Generates equipment loot once into pendingReward (deterministic via rng).
 */
export function enterRewardPhase(
  run: DungeonRunState,
  rng: SeededRng,
): DungeonRunState {
  if (run.encounter.phase !== 'won') {
    throw new Error('Reward phase requires a won encounter');
  }
  if (run.phase !== 'combat') {
    throw new Error('Reward phase can only follow combat');
  }
  if (run.encounter.progress.pendingLevelUps > 0) {
    throw new Error('Resolve pending level-ups before collecting rewards');
  }

  const blueprint = blueprintAt(run.encounters, run.encounterIndex);
  const defeated = countDefeated(run.encounter);
  const synced = syncDungeonEncounter(run, run.encounter);

  const awarded = new Set(run.equipmentLootAwardedEncounterIds ?? []);
  let equipment: GeneratedLootItem[] = [];
  const dungeonLevel = run.dungeonLevel ?? 1;
  const mutation = run.mutation ?? 'standard';
  if (!awarded.has(blueprint.id)) {
    equipment = generateEncounterEquipmentLoot(rng, {
      dungeonLevel,
      mutation,
      encounterId: blueprint.id,
      enemyTemplateIds: blueprint.enemyTemplateIds,
      defeatedEnemies: run.encounter.enemies
        .filter((e) => e.hp <= 0)
        .map((e) => ({ id: e.id, templateId: e.templateId })),
      includeBossChest: Boolean(blueprint.bossChestPlaceholder),
    });
    awarded.add(blueprint.id);
  }

  return {
    ...synced,
    phase: 'reward',
    pendingReward: {
      gold: blueprint.rewardGold,
      materials: blueprint.rewardMaterials,
      bossChest: Boolean(blueprint.bossChestPlaceholder),
      encounterId: blueprint.id,
      isBoss: Boolean(blueprint.isBoss),
      equipment,
    },
    equipmentLootAwardedEncounterIds: [...awarded],
    dungeonLevel,
    mutation,
    playLog: appendPlayLog(synced.playLog, [
      `Encounter cleared (${run.encounterIndex + 1}/${run.encounterCount})`,
      ...(equipment.length > 0
        ? [`Equipment drops: ${equipment.length}`]
        : []),
    ]),
    stats: {
      ...synced.stats,
      enemiesDefeated: synced.stats.enemiesDefeated + defeated,
      peakTempLevel: Math.max(
        synced.stats.peakTempLevel,
        run.encounter.progress.level,
      ),
    },
  };
}

/**
 * Collect the pending reward and either advance to the next encounter
 * (preserving HP / temp progression — no free heal) or finish the dungeon.
 */
export function collectRewardAndAdvance(
  run: DungeonRunState,
  rng: SeededRng,
): DungeonRunState {
  if (run.phase !== 'reward' || !run.pendingReward) {
    throw new Error('No pending reward to collect');
  }

  const reward = run.pendingReward;
  const rewards: RunRewards = {
    gold: run.rewards.gold + reward.gold,
    materials: run.rewards.materials + reward.materials,
    bossChests: run.rewards.bossChests + (reward.bossChest ? 1 : 0),
  };
  const encountersCleared = run.stats.encountersCleared + 1;
  const nextIndex = run.encounterIndex + 1;
  const rewardLine = formatRewardLog({
    gold: reward.gold,
    materials: reward.materials,
    bossChest: reward.bossChest,
    isBoss: reward.isBoss,
  });

  if (reward.isBoss || nextIndex >= run.encounterCount) {
    return {
      ...run,
      phase: 'results',
      outcome: 'victory',
      rewards,
      pendingReward: null,
      playLog: appendPlayLog(run.playLog ?? [], [
        rewardLine,
        'Dungeon cleared!',
      ]),
      stats: {
        ...run.stats,
        encountersCleared,
        peakTempLevel: Math.max(
          run.stats.peakTempLevel,
          run.encounter.progress.level,
        ),
      },
    };
  }

  const nextBlueprint = blueprintAt(run.encounters, nextIndex);
  const nextEncounter = buildEncounter(rng, nextBlueprint, {
    unlockedDiceSlots: run.unlockedDiceSlots,
    playerMaxHp: run.encounter.player.maxHp,
    playerHp: run.encounter.player.hp,
    progress: run.encounter.progress,
    tray: run.encounter.tray,
    combatStats: run.encounter.combatStats,
    unlockedActiveSkillIds: run.encounter.unlockedActiveSkillIds,
    dungeonLevel: run.dungeonLevel ?? 1,
    mutation: run.mutation ?? 'standard',
    equipmentSnapshot: run.equipmentSnapshot ?? null,
  });
  const banner = formatEncounterBanner(
    nextIndex,
    run.encounterCount,
    Boolean(nextBlueprint.isBoss),
  );
  const withReward = appendPlayLog(run.playLog ?? [], [rewardLine, banner]);
  const absorbed = absorbEncounterIntoPlayLog(withReward, 0, nextEncounter.log);

  return {
    ...run,
    encounterIndex: nextIndex,
    phase: 'combat',
    outcome: 'ongoing',
    encounter: nextEncounter,
    rewards,
    pendingReward: null,
    playLog: absorbed.playLog,
    playLogCursor: absorbed.cursor,
    stats: {
      ...run.stats,
      encountersCleared,
      peakTempLevel: Math.max(
        run.stats.peakTempLevel,
        nextEncounter.progress.level,
      ),
    },
  };
}

/** Mark the dungeon as defeated after the encounter is lost. */
export function enterDefeatResults(run: DungeonRunState): DungeonRunState {
  if (run.encounter.phase !== 'lost') {
    throw new Error('Defeat results require a lost encounter');
  }
  const synced = syncDungeonEncounter(run, run.encounter);
  return {
    ...synced,
    phase: 'results',
    outcome: 'defeat',
    pendingReward: null,
    playLog: appendPlayLog(synced.playLog, ['Run failed…']),
    stats: {
      ...synced.stats,
      enemiesDefeated:
        synced.stats.enemiesDefeated + countDefeated(run.encounter),
      peakTempLevel: Math.max(
        synced.stats.peakTempLevel,
        run.encounter.progress.level,
      ),
    },
  };
}

export function encounterProgressLabel(run: DungeonRunState): string {
  return `Encounter ${run.encounterIndex + 1}/${run.encounterCount}`;
}

export function isBossEncounter(run: DungeonRunState): boolean {
  const bp = run.encounters[run.encounterIndex];
  return Boolean(bp?.isBoss);
}

export function describeDungeonResults(run: DungeonRunState): string[] {
  const lines = [
    run.outcome === 'victory' ? 'Dungeon cleared!' : 'Run failed…',
    `${run.stats.encountersCleared}/${run.encounterCount} encounters cleared`,
    `Enemies defeated: ${run.stats.enemiesDefeated}`,
    `Peak temp level: ${run.stats.peakTempLevel}`,
    `Gold: ${run.rewards.gold} · Materials: ${run.rewards.materials}`,
  ];
  if (run.rewards.bossChests > 0) {
    lines.push(`Boss chests (placeholder): ${run.rewards.bossChests}`);
  }
  lines.push('Temp skills & XP reset on the next dungeon.');
  return lines;
}
